import { eventScope } from '../core/ai-instructions';
import { parseAiNotes } from '../core/ai-notes';
import { discover } from '../core/footage';
import { formatDate, formatTime, placeLabel } from '../core/format';
import { parseEventJson } from '../core/reasons';
import type { ClipScan } from '../core/scan';
import type { Timeline } from '../core/timeline';
import { AiDialog } from './ai-dialog';
import { Autoplay } from './autoplay';
import { commandFor, SORT_TITLES, type Command } from './commands';
import { Deleter } from './deleter';
import { demoProgressText, openedFromDemo } from './demo';
import { readDom, type AppDom } from './dom';
import { ExportDialog } from './export-dialog';
import { ensureReadable, rememberFolder, rememberedFolder } from './folders';
import { Library, type LibraryFile, type LibraryFootage } from './library';
import { EventList } from './list';
import { captureDrop, openedFromDrop, openedFromHandle, openedFromInput, type Opened } from './open';
import { Palette, renderShortcuts } from './palette';
import { PRIORITY_TRIAGE, PRIORITY_VIEWER, ScanQueue, type ScanRequest } from './queue';
import { ScanPool, scanWorkerCount } from './scanner';
import { SingleVideoView } from './single';
import { EventViewer } from './viewer';

const LIST_REFRESH_MS = 400;
const READING_FOLDER = 'Reading folder…';
const DEMO_PARAMETER = 'demo';
const TOAST_MS = 2600;
const INCIDENT_TIER = 3;

type Mode = 'empty' | 'library' | 'single';

interface TimelineWaiter {
  readonly footage: LibraryFootage;
  readonly resolve: (timeline: Timeline<LibraryFile> | undefined) => void;
}

interface Session {
  readonly name: string;
  readonly library: Library;
  readonly list: EventList;
  readonly pool: ScanPool;
  readonly queue: ScanQueue;
  readonly root: FileSystemDirectoryHandle | undefined;
  readonly waiters: TimelineWaiter[];
  selected: LibraryFootage | undefined;
  viewer: EventViewer | undefined;
  autostart: boolean;
  refresh: number | undefined;
}

async function readSidecars(library: Library, footage: LibraryFootage): Promise<void> {
  const [event, notes] = await Promise.all([footage.eventJson?.file.text(), footage.aiJson?.file.text()]);
  library.setInfo(footage.id, event === undefined ? undefined : parseEventJson(event));
  library.setNotes(footage.id, notes === undefined ? undefined : parseAiNotes(notes));
}

const canRemove = (root: FileSystemDirectoryHandle | undefined): root is FileSystemDirectoryHandle =>
  root !== undefined && typeof root.removeEntry === 'function';

class App {
  private session: Session | undefined;
  private single: SingleVideoView | undefined;
  private readonly exportDialog: ExportDialog;
  private readonly aiDialog: AiDialog;
  private readonly deleter: Deleter;
  private readonly autoplay: Autoplay;
  private readonly palette: Palette;
  private toastTimer = 0;
  private dragDepth = 0;

  constructor(
    private readonly dom: AppDom,
    private readonly scanWorkerUrl: string,
    exportWorkerUrl: string,
    private readonly demoUrl: URL,
  ) {
    this.exportDialog = new ExportDialog(dom.exportDialog, exportWorkerUrl, dom.fonts.href);
    this.aiDialog = new AiDialog(dom.aiDialog, (message) => this.toast(message), navigator.clipboard);
    this.deleter = new Deleter(dom.deleter, { hide: (footage) => this.hide(footage), restore: (footage) => this.restore(footage), notify: (message) => this.toast(message) });
    this.autoplay = new Autoplay(dom.autoplay);
    this.palette = new Palette(dom.palette, (command) => this.available(command), (command) => this.run(command));
    renderShortcuts(dom.shortcuts);
    this.bindOpening();
    this.bindDragAndDrop();
    this.bindButtons();
    document.addEventListener('keydown', (event) => this.onKey(event));
    void this.offerReopen();
    if (new URLSearchParams(location.search).has(DEMO_PARAMETER)) void this.openDemo();
  }

  private bindButtons(): void {
    const { dom } = this;
    dom.help.addEventListener('click', () => this.run({ kind: 'help' }));
    dom.paletteButton.addEventListener('click', () => this.run({ kind: 'palette' }));
    dom.exportButton.addEventListener('click', () => this.run({ kind: 'export' }));
    dom.deleteButton.addEventListener('click', () => this.run({ kind: 'delete' }));
    dom.viewerDom.ai.addEventListener('click', () => this.run({ kind: 'ai' }));
    document.querySelectorAll<HTMLButtonElement>('dialog [data-close]').forEach((button) => button.addEventListener('click', () => button.closest('dialog')?.close()));
  }

  private setMode(mode: Mode): void {
    this.dom.app.dataset['mode'] = mode;
    this.dom.dropZone.hidden = mode !== 'empty';
    this.dom.viewer.hidden = mode !== 'library';
    this.dom.single.hidden = mode !== 'single';
  }

  private bindOpening(): void {
    const pickFolder = (): void => void this.pickFolder();
    this.dom.openFolder.addEventListener('click', pickFolder);
    this.dom.dropOpen.addEventListener('click', pickFolder);
    this.dom.folderInput.addEventListener('change', () => this.openFromInput(this.dom.folderInput));
    this.dom.fileInput.addEventListener('change', () => this.openFromInput(this.dom.fileInput));
    this.dom.demo.addEventListener('click', () => void this.openDemo());
  }

  private openFromInput(input: HTMLInputElement): void {
    if (input.files?.length) void this.open(openedFromInput(input.files));
    input.value = '';
  }

  private async pickFolder(): Promise<void> {
    if (!window.showDirectoryPicker) {
      this.dom.folderInput.click();
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({ id: 'teslacam', mode: 'read' });
      await this.open(await this.reading(openedFromHandle(handle)));
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) this.toast(`Could not open the folder: ${String(error)}`);
    }
  }

  private async offerReopen(): Promise<void> {
    const handle = await rememberedFolder();
    if (!handle) return;
    this.dom.reopenName.textContent = handle.name;
    this.dom.reopen.hidden = false;
    this.dom.reopen.addEventListener('click', async () => {
      if (await ensureReadable(handle)) await this.open(await this.reading(openedFromHandle(handle)));
      else this.toast('Reading that folder needs your permission again. Open it from the button instead.');
    });
  }

  private bindDragAndDrop(): void {
    const target = document.documentElement;
    target.addEventListener('dragenter', (event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      this.dragDepth++;
      this.dom.app.toggleAttribute('data-dragging', true);
    });
    target.addEventListener('dragover', (event) => {
      if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
    });
    target.addEventListener('dragleave', () => {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dom.app.removeAttribute('data-dragging');
    });
    target.addEventListener('drop', (event) => {
      event.preventDefault();
      this.dragDepth = 0;
      this.dom.app.removeAttribute('data-dragging');
      if (event.dataTransfer) void this.openDrop(event.dataTransfer);
    });
  }

  private async openDrop(transfer: DataTransfer): Promise<void> {
    const pending = captureDrop(transfer);
    if (pending.entries.length > 0) await this.open(await this.reading(openedFromDrop(pending)));
  }

  private async openDemo(): Promise<void> {
    const opening = openedFromDemo(this.demoUrl, (progress) => {
      this.dom.dropBusy.textContent = demoProgressText(progress);
    });
    try {
      await this.open(await this.reading(opening, demoProgressText({ loaded: 0, total: 0 })), true);
    } catch (error) {
      this.toast(`Could not load the demo: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private async reading(opening: Promise<Opened>, label = READING_FOLDER): Promise<Opened> {
    this.dom.dropBusy.textContent = label;
    this.dom.app.toggleAttribute('data-busy', true);
    try {
      return await opening;
    } finally {
      this.dom.app.removeAttribute('data-busy');
    }
  }

  private async open(opened: Opened, autostart = false): Promise<void> {
    const { footage, videos } = discover(opened.files);
    if (footage.length === 0 && videos.length === 0) {
      this.toast(`No dashcam clips or videos in ${opened.name}.`);
      return;
    }
    this.close();
    if (opened.handle) void rememberFolder(opened.handle);
    if (footage.length > 0) await this.openLibrary(opened.name, footage, opened.handle, autostart);
    else this.openVideos(videos);
  }

  private close(): void {
    const session = this.session;
    if (session) {
      session.viewer?.destroy();
      session.pool.terminate();
      session.list.destroy();
      window.clearTimeout(session.refresh);
    }
    this.autoplay.cancel();
    this.session = undefined;
    this.single?.destroy();
    this.single = undefined;
  }

  private async openLibrary(name: string, footage: readonly LibraryFootage[], root: FileSystemDirectoryHandle | undefined, autostart: boolean): Promise<void> {
    const library = new Library(footage);
    await Promise.all(footage.map((item) => readSidecars(library, item)));
    const workers = scanWorkerCount();
    const pool = new ScanPool(this.scanWorkerUrl, workers);
    const list = new EventList(
      { list: this.dom.list, chips: this.dom.chips, search: this.dom.search, sort: this.dom.sort, empty: this.dom.listEmpty },
      { select: (id) => this.select(id), remove: (id) => void this.remove(id) },
    );
    const queue = new ScanQueue(pool.run, workers, (request, result) => this.onScanned(request, result));
    const session: Session = { name, library, list, pool, queue, root, waiters: [], selected: undefined, viewer: undefined, autostart: false, refresh: undefined };
    this.session = session;
    this.setMode('library');
    list.setDeletable(canRemove(root));
    this.dom.deleteButton.hidden = !canRemove(root);
    list.update(library.items());
    const first = list.neighbour(1, undefined);
    if (first) this.select(first, autostart);
    queue.add(library.footage.flatMap((item) => item.clips.filter((clip) => clip.camera === 'front')), PRIORITY_TRIAGE);
    this.showProgress();
  }

  private openVideos(videos: readonly LibraryFile[]): void {
    this.setMode('single');
    this.single = new SingleVideoView(this.dom.singleDom, videos, (files) => this.exportDialog.openMerge(files), (message) => this.toast(message));
  }

  private select(footageId: string, autostart = false): void {
    const session = this.session;
    const footage = session?.library.footageById(footageId);
    if (!session || !footage) return;
    this.autoplay.cancel();
    session.viewer?.destroy();
    session.viewer = undefined;
    session.selected = footage;
    session.autostart = autostart;
    session.list.select(footageId);
    this.dom.viewer.hidden = false;
    this.dom.dropZone.hidden = true;
    this.dom.viewer.toggleAttribute('data-loading', true);
    session.queue.add(footage.clips, PRIORITY_VIEWER);
    this.mountViewer();
  }

  private mountViewer(): void {
    const session = this.session;
    const footage = session?.selected;
    if (!session || !footage || session.viewer) return;
    const timeline = session.library.timeline(footage);
    if (!timeline) {
      if (footage.clips.every((clip) => session.library.settled(clip))) this.toast('None of the clips of this event could be read.');
      return;
    }
    this.dom.viewer.removeAttribute('data-loading');
    session.viewer = new EventViewer(this.dom.viewerDom, session.library, footage, timeline, {
      tier: session.library.entry(footage).tier,
      autostart: session.autostart,
      onEnded: () => this.offerNext(),
      onEngaged: () => this.autoplay.cancel(),
    });
  }

  private offerNext(): void {
    const session = this.session;
    const next = session?.list.neighbour(1);
    const entry = session?.list.entryById(next);
    if (!next || !entry) return;
    this.autoplay.offer(placeLabel(entry.info), () => this.select(next, true));
  }

  private onScanned(request: ScanRequest, result: ClipScan | Error): void {
    const session = this.session;
    if (!session) return;
    session.library.setResult(request.clip, result);
    this.showProgress();
    this.mountViewer();
    this.settleWaiters(session);
    if (request.clip.camera === 'front') this.refreshList(session);
  }

  private refreshList(session: Session): void {
    if (session.refresh !== undefined) return;
    session.refresh = window.setTimeout(() => {
      session.refresh = undefined;
      session.list.update(session.library.items());
    }, LIST_REFRESH_MS);
  }

  private settleWaiters(session: Session): void {
    for (const waiter of [...session.waiters]) {
      if (!waiter.footage.clips.every((clip) => session.library.settled(clip))) continue;
      session.waiters.splice(session.waiters.indexOf(waiter), 1);
      waiter.resolve(session.library.timeline(waiter.footage));
    }
  }

  private timelineOf(footage: LibraryFootage): Promise<Timeline<LibraryFile> | undefined> {
    const session = this.session;
    if (!session) return Promise.resolve(undefined);
    const ready = session.library.timeline(footage);
    if (ready) return Promise.resolve(ready);
    session.queue.add(footage.clips, PRIORITY_VIEWER);
    return new Promise((resolve) => {
      session.waiters.push({ footage, resolve });
      this.settleWaiters(session);
    });
  }

  private showProgress(): void {
    const progress = this.session?.library.frontProgress();
    const busy = progress !== undefined && progress.done < progress.total;
    this.dom.progress.hidden = !busy;
    if (!progress || !busy) return;
    this.dom.progressFill.style.width = `${(progress.done / Math.max(1, progress.total)) * 100}%`;
    this.dom.progressText.textContent = `Reading telemetry · ${progress.done} of ${progress.total} front clips`;
  }

  private async remove(footageId: string | undefined): Promise<void> {
    const session = this.session;
    const entry = session?.list.entryById(footageId);
    if (!session || !entry) return;
    if (!canRemove(session.root)) {
      this.toast('Deleting works in Chrome when the folder was opened with Open folder or dropped onto the page.');
      return;
    }
    const incident = entry.tier <= INCIDENT_TIER ? (entry.info?.reason.label ?? 'an incident') : undefined;
    const title = `${entry.info?.reason.label ?? 'Recording'} · ${placeLabel(entry.info)} · ${formatDate(entry.time)} ${formatTime(entry.time).slice(0, 5)}`;
    session.viewer?.pause();
    await this.deleter.request(session.root, { footage: entry.footage, title, incident });
  }

  private hide(footage: LibraryFootage): void {
    const session = this.session;
    if (!session) return;
    const wasSelected = session.selected?.id === footage.id;
    const next = wasSelected ? (session.list.neighbour(1) ?? session.list.neighbour(-1)) : undefined;
    session.library.hide(footage);
    session.list.update(session.library.items());
    if (!wasSelected) return;
    if (next && next !== footage.id) {
      this.select(next, this.autoplay.enabled);
      return;
    }
    session.viewer?.destroy();
    session.viewer = undefined;
    session.selected = undefined;
    this.dom.viewer.hidden = true;
    this.dom.dropZone.hidden = false;
  }

  private restore(footage: LibraryFootage): void {
    const session = this.session;
    if (!session) return;
    session.library.restore(footage);
    session.list.update(session.library.items());
    this.select(footage.id);
  }

  private openExport(): void {
    const session = this.session;
    const viewer = session?.viewer;
    if (!session || !viewer) return;
    viewer.pause();
    this.exportDialog.open({ library: session.library, viewer, items: session.library.items(), timelineOf: (footage) => this.timelineOf(footage) });
  }

  private openAi(): void {
    const session = this.session;
    const viewer = session?.viewer;
    if (!session || !viewer) return;
    viewer.pause();
    this.aiDialog.open({
      notes: session.library.notesOf(viewer.footage),
      event: eventScope(viewer.footage, session.library.infoOf(viewer.footage)),
      root: session.name,
      utcOffset: viewer.timeline.utcOffset,
      jump: (time) => viewer.jumpTo(time),
    });
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) void document.exitFullscreen();
    else this.dom.stage.requestFullscreen().catch(() => this.toast('This browser refused fullscreen.'));
  }

  private available(command: Command): boolean {
    const session = this.session;
    const viewing = session?.viewer !== undefined;
    switch (command.kind) {
      case 'palette':
      case 'help':
      case 'open-folder':
      case 'toggle-autoplay':
        return true;
      case 'delete':
        return viewing && canRemove(session?.root);
      case 'event':
      case 'search':
      case 'chip':
      case 'sort':
      case 'sort-cycle':
      case 'toggle-session':
        return session !== undefined;
      case 'map':
        return viewing && !this.dom.viewerDom.mapLink.hidden;
      default:
        return viewing;
    }
  }

  private run(command: Command): boolean {
    if (command.kind !== 'toggle-autoplay') this.autoplay.cancel();
    switch (command.kind) {
      case 'palette':
        this.palette.open();
        return true;
      case 'help':
        this.dom.helpDialog.showModal();
        return true;
      case 'open-folder':
        void this.pickFolder();
        return true;
      case 'toggle-autoplay':
        this.autoplay.toggle();
        this.toast(this.autoplay.enabled ? 'Autoplay on' : 'Autoplay off');
        return true;
      case 'delete':
        void this.remove(this.session?.selected?.id);
        return true;
      case 'export':
        this.openExport();
        return true;
      case 'ai':
        this.openAi();
        return true;
      case 'fullscreen':
        this.toggleFullscreen();
        return true;
      case 'map':
        window.open(this.dom.viewerDom.mapLink.href, '_blank', 'noopener,noreferrer');
        return true;
      default:
        return this.runInLibrary(command);
    }
  }

  private runInLibrary(command: Command): boolean {
    const session = this.session;
    if (!session) return false;
    switch (command.kind) {
      case 'event': {
        const next = session.list.neighbour(command.direction);
        if (next) this.select(next);
        return true;
      }
      case 'search':
        this.dom.search.focus();
        this.dom.search.select();
        return true;
      case 'chip':
        session.list.setChip(command.chip);
        return true;
      case 'sort':
        session.list.setSort(command.order);
        return true;
      case 'sort-cycle':
        this.toast(`Sorted by ${SORT_TITLES[session.list.cycleSort()].toLowerCase()}`);
        return true;
      case 'toggle-session':
        session.list.toggleSession(session.selected?.id);
        return true;
      default:
        return session.viewer?.command(command) ?? false;
    }
  }

  private onKey(event: KeyboardEvent): void {
    const target = event.target;
    const typing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
    if (this.autoplay.counting && !typing && (event.key === 'Enter' || event.key === 'Escape')) {
      event.preventDefault();
      if (event.key === 'Enter') this.autoplay.fire();
      else this.autoplay.cancel();
      return;
    }
    const command = commandFor(event);
    if (!command) return;
    if (command.kind === 'palette' && !this.dom.palette.dialog.open) {
      event.preventDefault();
      this.run(command);
      return;
    }
    if (typing || document.querySelector('dialog[open]')) return;
    if (this.available(command) && this.run(command)) event.preventDefault();
  }

  private toast(message: string): void {
    this.dom.toast.textContent = message;
    this.dom.toast.toggleAttribute('data-visible', true);
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.dom.toast.removeAttribute('data-visible'), TOAST_MS);
  }
}

const script = document.querySelector<HTMLScriptElement>('script[data-scan-worker]');
new App(readDom(), script?.dataset['scanWorker'] ?? '', script?.dataset['exportWorker'] ?? '', new URL(script?.dataset['demo'] ?? '', location.href));
